import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, NO_ERRORS_SCHEMA, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogContentBase, DialogModule, DialogRef } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { CasiCloseOptions, defaultGridPageSize, FormModes } from '../common/constants.lib';
import { Release, Project, User, CasiOwnerUser } from '../models/models.lib';
import { AutoFocusDirective } from '../directives/auto-focus.directive';
import { LoggerService } from '../shared/logger.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProjectUserService } from '../tracker/project-user.service';
import { CompositeFilterDescriptor, SortDescriptor, distinct, filterBy, orderBy, } from '@progress/kendo-data-query';
import { MulticheckFilterComponent } from '../filters/multicheck-filter/multicheck-filter.component';
import { GridDataResult, GridModule, KENDO_GRID, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { ReleaseService } from './release.service';
import { formatText } from '../common/utils.lib';

@Component({
  selector: 'app-release',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    RouterLink, RouterLinkActive,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    DateInputsModule,
    LayoutModule,
    AutoFocusDirective,
    GridModule
  ],
  templateUrl: './release.component.html',
  styleUrl: './release.component.scss',
  schemas: [NO_ERRORS_SCHEMA]
})
export class ReleaseComponent implements OnInit{

 // public projects: Project[] = [];
  private _projects: Project[] = [];
  private _users: User[] = [];
  public currentProject: Project | undefined = undefined;
  public minDate: Date = new Date(2015, 0, 1);
  public maxDate: Date = new Date();
  public selectLabel: string = 'Select All';
  public selectedStoryIds: string[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public rawData: any[] = [];
  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  //public pageSizes = [5, 10, 25, 50, 100];
  public previousNext = false;
  public position: PagerPosition = "bottom";
  public pageSize = defaultGridPageSize;
  public skip = 0;
  public buttonCount = 0;
  public formatText = formatText;
  public toggleFlag: boolean = false;
  public loading: boolean = false;
  public FormModes = FormModes;

  public sort: SortDescriptor[] = [
    {
      field: "id",
      dir: "desc",
    },
  ];

  public gridData: any[] = filterBy(this.rawData, this.filter);

  @Output() submitForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() closeForm: EventEmitter<void> = new EventEmitter();

  private _release: Release | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set release(val: Release) {
    this._release = val;
    this.form.patchValue(this._release);
  }
  get release(): Release | undefined {
    return this._release;
  };

  @Input({ required: true }) projectId?: number = undefined;

  // @Input({ required: true }) set projects(val: Project[]) {
  //   this._projects = val;
  //   //this.form.patchValue(this._release);
  // }
  // get projects(): Project[] {
  //   return this._projects;
  // };

  set users(val: User[]) {
    this._users = val;
    //this.form.patchValue(this._release);
  }
  get users(): User[] {
    return this._users;
  };

  private _mode: FormModes = FormModes.NOTSET;
  @Input({ required: true }) set mode(val: FormModes) {
    this._mode = val;
  }
  get mode(): FormModes {
    return this._mode;
  };

  form: FormGroup;

  private _subs: Subscription[] = [];

  constructor(
   // public override dialog: DialogRef,
    private fb: FormBuilder,
    private logger: LoggerService,
    private projectUserService: ProjectUserService,
    private releaseService: ReleaseService
  ) {
   // super(dialog);
    this.form = this.fb.group(
      {
        id: [null, []],
        project_id: [null, [Validators.required]],
        comment: [null, [Validators.required]],
        released_by_user_id: [null, [Validators.required]],
        release_date: [this.maxDate, [Validators.required]],
        selectedStoryIds: [null, []]
      }
    );
  }

  //#region Form

  get f() {
    return this.form.controls;
  }
  get comment() {
    return this.form.get('comment');
  }
  get released_by_user_id () {
    return this.form.get('released_by_user_id');
  }
  get release_date () {
    return this.form.get('release_date');
  }

  //#endregion

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if (this.projectId) {
      this.fetchProjectUsers(this.projectId);
      if (this.mode == FormModes.NEW) {
        this.loading = true;
        this.releaseService.fetchForRelease(this.projectId)
        .then(data => {
          if (data) {
            this.rawData = data;
            this.loadItems();
          } else {
            this.rawData = [];
            this.loadItems();
          }
        })
        .catch(err => {
          this.logger.error(`doDataRequest:error: `, err);
          this.loading = false;
        });
      }

    }
    this.form.patchValue({release_date: this.release?.release_date ? new Date(this.release.release_date) : this.maxDate, project_id: this.projectId});

    // this._subs.push(this.route.data.subscribe(({
    //   projects,
    //   users
    // }) => {
    //   this.projects = projects;
    //   this.users = users;
    //   this.logger.debug(`ngOnInit:projects: `, this.projects);
    //   this.logger.debug(`ngOnInit:users: `, this.users);

    // }));
  }

  public defaultProjectItem: { text: string; id: number | null } = {
    text: "Select ...",
    id: null
  }

  public userValueChange(value: any): void {
    this.logger.debug(`userValueChange...`, value);
    // if (value && value.id) {
    //   this.currentU = value;
    //   this.fetchProjectUsers(value.id);
    // }
    // else {
    //   this.currentProject = undefined;
    // }
  }

  public projectValueChange(value: any): void {
    this.logger.debug(`projectValueChange...`, value);
    this.form.patchValue({released_by_user_id: null});
    if (value) {
      // this.currentProject = this.projects.find((v) => {
      //   return v.id == value
      // });
      this.fetchProjectUsers(value);
    }
    else {
      this.users = [];
      //this.currentProject = undefined;
    }
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadItems();
    // this.fetchUserStories(this.selectedProject);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadItems();
    // this.fetchUserStories(this.selectedProject);
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  private loadItems(): void {
    this.logger.debug(`loadItems: `)
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
    let count = 0;
    this.selectedStoryIds = [];
    this.gridData.map((d) => {
      if (d.selected) {
        this.selectedStoryIds.push(d.id);
        count++;
      }
    });
    if (count == this.gridData.length)
      this.toggleFlag = true;

    this.form.patchValue({selectedStoryIds: this.selectedStoryIds})

    this.loading = false;
  }


  private fetchProjectUsers(projectId: number) {
    return this.projectUserService.getByProjectId(projectId)
      .then(resp => {
        this.users = resp.map((r: any) => CasiOwnerUser.createFromProjectUser(r));
      })
      .catch(err => {
        this.logger.debug(`fetchProjectUsers: `, err.message);
      })
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.closeForm.emit();
   // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`, this.form.value);
    this.submitForm.emit(this.form);
    //if (this.form.status !== 'INVALID')
    //  this.dialog.close({ text: CasiCloseOptions.Saved, selectedStoryIds: this.selectedStoryIds });
  }

  public toggleAll(): void {
    this.logger.debug('toggleAll...');
    this.toggleFlag = !this.toggleFlag;
    this.gridData.forEach(gd => gd.selected = this.toggleFlag);
    this.selectedStoryIds = this.gridData.filter(f => f.selected === true).map(gd => gd.id);
    this.logger.debug('toggleAll', this.selectedStoryIds);
    this.form.patchValue({selectedStoryIds: this.selectedStoryIds})
  }

  public setSelection(dataItem: any) {
    this.logger.debug('set dataitem', dataItem);
    const { id } = dataItem;
    if (!dataItem.selected) {
      dataItem.selected = true;
      if (this.selectedStoryIds.indexOf(id) == -1) {
        this.selectedStoryIds.push(id);
      }
    }
    else {
      dataItem.selected = false;
      if (this.selectedStoryIds.indexOf(id) != -1) {
        this.selectedStoryIds.splice(this.selectedStoryIds.indexOf(id), 1);
      }
    }

    this.form.patchValue({selectedStoryIds: this.selectedStoryIds})

    this.logger.debug(`selected items`, {
      selectedStoryIds: this.selectedStoryIds,
      dataItem, id
    });
  }
}
